'use client';

import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, InputRef } from 'antd';

import { Input, Button } from 'components';

import FormButtons from 'modules/form-components/buttons/FormButtons';

import useForm from 'hooks/forms/useForm';
import useMyProfile from 'hooks/admin/useMyProfile';
import useValidation from 'hooks/forms/useValidation';
import useAutoFocus from 'hooks/elements/useAutoFocus';
import useNotifications from 'hooks/common/useNotifications';
import { useDisable2FaMutation } from '../../hooks/useDisable2FaMutation';

import { InputVariant } from 'components/inputs/Input';
import { IDisable2FaFormProps } from './Disable2FaForm.types';

import './Disable2FaForm.styles.scss';

const Disable2FaForm = ({ onFinish, onError }: IDisable2FaFormProps) => {
  const inputRef = useRef<InputRef | null>(null);
  const rules = useValidation();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { notificationApi } = useNotifications();

  const { refetch } = useMyProfile();
  const { loading, refetch: disableUser2Fa } = useDisable2FaMutation();

  const { submitDisabled } = useForm(form);

  useAutoFocus(inputRef);

  const [recoveryMode, setRecoveryMode] = useState(false);

  const handleFinish = () => {
    form.resetFields();
    refetch();
    setRecoveryMode(false);
    onFinish();
  };

  const handleError = ({ errorMessage }) => {
    form.resetFields();
    setRecoveryMode(false);
    onError();
    notificationApi.error({ title: t(errorMessage) });
  };

  const handleSubmit = async ({ totp, otp }) => {
    const { success, errorMessage } = await disableUser2Fa({ totp, otp });

    if (success) {
      handleFinish();
    } else {
      handleError({ errorMessage });
    }
  };

  return (
    <>
      <Form
        className="np-disable-2fa-form__body"
        layout="vertical"
        form={form}
        name="disable-2fa"
        onFinish={handleSubmit}
        scrollToFirstError
        requiredMark="optional"
      >
        {!recoveryMode ? (
          <Form.Item
            name="totp"
            validateFirst
            rules={rules.ga}
            label={t('form.label.gaCode')}
            className="np-disable-2fa-form__totp"
          >
            <Input autoFocus disabled={loading} ref={inputRef} variant={InputVariant.BORDERED} />
          </Form.Item>
        ) : (
          <Form.Item
            name="otp"
            validateFirst
            rules={rules.ga}
            label={t('form.label.recoveryCode')}
            className="np-disable-2fa-form__totp"
          >
            <Input autoFocus disabled={loading} variant={InputVariant.BORDERED} />
          </Form.Item>
        )}

        {!recoveryMode && (
          <Button type="link" onClick={() => setRecoveryMode(true)} className="np-disable-2fa-form__link">
            {t('common.forgotLinks.gaNoAccess')}
          </Button>
        )}

        <FormButtons submitDisabled={submitDisabled} loading={loading} submitLabel="form.button.submit" />
      </Form>
    </>
  );
};

export default Disable2FaForm;
